import { PdfH2 } from 'pages/v2/compiler/PDF/Styles.styled';
import { Column } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import { useEpdScenarioModule, useEpdScenarioUnits } from 'services/api/queries';
import styled, { css } from 'styled-components';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { ScenarioItemModel, ScenarioModuleItemModel, ScenarioModuleRequest } from 'types/types';

import { VersionStateType } from 'services/EpdClient';
import { DataTableStyled } from '../grids/StyledGrid';
import { getScenarioModuleGridRows } from '../grids/scenarios/ScenarioModuleDescription';
import { ScenarioModuleItem } from '../version-history/content/VersionHistoryScenariosSection';
import { EpdVersionHistoryData } from '../version-history/utils/EpdVersionHistoryDataContext';
import VersionCompareKeyValueGrid from './VersionCompareKeyValueGrid';
import { VersionCompareRow } from './VersionCompareRow';

type TProps = {
  versionData: { left: EpdVersionHistoryData | null; right: EpdVersionHistoryData | null };
  scenarioData: { left: ScenarioItemModel; right: ScenarioItemModel };
  moduleData: { left: ScenarioModuleItem | null | undefined; right: ScenarioModuleItem | null | undefined };
};

const CompareScenarioModuleSection = ({ versionData, scenarioData, moduleData }: TProps) => {
  const { t } = useTranslation();

  const units = useEpdScenarioUnits().data;

  const descriptionBodyTemplate = (rowData: ScenarioModuleItemModel) => {
    return rowData?.mandatory ? (
      <DescriptionCell
        readonly
        dangerouslySetInnerHTML={{
          __html: t(`epdCompiler.step3.scenarioTab.epdScenarioModule.${rowData.module}.description.${rowData.placeholder}`),
        }}
      />
    ) : (
      <DescriptionCell readonly={false}>{rowData?.description ?? <>&nbsp;</>}</DescriptionCell>
    );
  };

  const unitBodyTemplate = (rowData: ScenarioModuleItemModel) => {
    const option = units?.find((x) => x.value === rowData.unitId);
    return option && option !== null && <div dangerouslySetInnerHTML={{ __html: option.label }} />;
  };

  return (
    <>
      <VersionCompareRow data={moduleData} displayCondition={(x) => !!x}>
        {({ item, isLeft }) => {
          return (
            <PdfH2 style={{ fontSize: 12, color: '#000', textTransform: 'uppercase', marginBottom: '-20px' }}>
              {item?.descriptionLabel || ''}
            </PdfH2>
          );
        }}
      </VersionCompareRow>
      <VersionCompareRow data={moduleData} displayCondition={(x) => !!x}>
        {({ item, isLeft }) => {
          const moduleId = item?.moduleId || '';
          const moduleName = item?.dataGridModuleName || '';

          const isEPDCompleteLeft =
            versionData.left?.epdDefinitionInformation?.versionState === VersionStateType.Published ||
            versionData.left?.epdDefinitionInformation?.versionState === VersionStateType.Deregistered;
          const isEPDCompleteRight =
            versionData.right?.epdDefinitionInformation?.versionState === VersionStateType.Published ||
            versionData.right?.epdDefinitionInformation?.versionState === VersionStateType.Deregistered;
          const scenarioModuleLeft = useEpdScenarioModule({
            epdVersionId: versionData?.left?.epdVersionId || '',
            scenarioId: scenarioData?.left?.id,
            module: moduleId,
          } as ScenarioModuleRequest).data;

          const scenarioModuleRight = useEpdScenarioModule({
            epdVersionId: versionData?.right?.epdVersionId || '',
            scenarioId: scenarioData?.right?.id,
            module: moduleId,
          } as ScenarioModuleRequest).data;

          const additionalData = {
            left: {
              module: moduleData?.left?.moduleId || '',
              moduleName: moduleData?.left?.dataGridModuleName || '',
              isDefaultScenario: scenarioData?.left?.isDefaultScenario,
              scenarioModule: scenarioModuleLeft,
              isEPDComplete: isEPDCompleteLeft,
            },
            right: {
              module: item?.moduleId || '',
              moduleName: moduleName,
              isDefaultScenario: scenarioData?.right?.isDefaultScenario,
              scenarioModule: scenarioModuleRight,
              isEPDComplete: isEPDCompleteRight,
            },
          };
          return (
            <VersionCompareKeyValueGrid
              isLeft={isLeft}
              additionalData={additionalData}
              rowsGetter={getScenarioModuleGridRows}
            />
          );
        }}
      </VersionCompareRow>
      <VersionCompareRow data={moduleData} displayCondition={(x) => !!x} compareByDom>
        {({ item, isLeft, compareClassName }) => {
          const module = item?.moduleId || '';
          const moduleName = item?.dataGridModuleName || '';

          const epdVersionId = isLeft ? versionData.left?.epdVersionId || '' : versionData.right?.epdVersionId || '';
          const scenarioId = isLeft ? scenarioData.left.id : scenarioData.right.id;

          const scenarioModule = useEpdScenarioModule({ epdVersionId, scenarioId, module } as ScenarioModuleRequest).data;

          const rows = scenarioModule?.scenarioModuleItems;
          const valuableRows = scenarioModule?.scenarioModuleItems?.filter((x) => x.unitId || x.value);
          const hasValuableRows = valuableRows && valuableRows.length > 0;
          const header = item?.dataGridCaption ? (
            <HeaderCaptionSemiBold>{item?.dataGridCaption}</HeaderCaptionSemiBold>
          ) : undefined;
          return hasValuableRows ? (
            <ModuleDataTableStyled
              value={hasValuableRows ? valuableRows : rows}
              dataKey="id"
              header={header}
              showGridlines
              className={compareClassName}
            >
              <Column
                field="description"
                header={module === 'RSL' ? `${moduleName} information` : `Module ${moduleName} information`}
                body={descriptionBodyTemplate}
                style={{ width: '40%' }}
              />
              <Column field="value" header="Value" className="padding" style={{ width: '40%' }} />
              <Column field="unitId" header="Unit" body={unitBodyTemplate} className="padding" style={{ width: '40%' }} />
            </ModuleDataTableStyled>
          ) : (
            <></>
          );
        }}
      </VersionCompareRow>
    </>
  );
};

const ModuleDataTableStyled = styled(DataTableStyled)`
  border-collapse: collapse;
  .p-datatable-table {
    height: 100%;
  }
  .p-datatable-tbody > tr > td {
    padding: 0;
  }
  .p-datatable-tbody > tr > td.padding,
  .p-datatable-tbody > tr.p-datatable-emptymessage > td {
    padding: 0.5rem 1rem;
  }
  && .p-inputtext.p-dropdown-label.p-placeholder {
    color: ${(props) => props.theme.colors.primaryBlack};
  }
  .p-checkbox.p-component.p-disabled > .p-checkbox-box.p-component {
    opacity: 1;
    border-color: ${(props) => props.theme.colors.elementsDropDown};
  }
`;

const CellContainer = styled.div<{ readonly: boolean }>`
  ${(props) =>
    props.readonly &&
    css`
      background-color: ${(props) => props.theme.colors.elementsFilledInput};
    `}
  padding: 0.5rem 1rem;
  height: 100%;
`;

const DescriptionCell = styled(CellContainer)`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export default CompareScenarioModuleSection;
